import { initializeConnect } from './initializeConnect';
import type { ConnectionStatus, InspectorError, InspectorEvent } from './types';

export type AwsConnectBridgeMode = 'disabled' | 'workspace';

export interface AwsConnectAgentIdentity {
  name?: string;
  arn?: string;
  routingProfileName?: string;
  routingProfileArn?: string;
  extension?: string;
  availabilityState?: string;
  lastUpdatedAt?: string;
}

export interface AwsConnectContactSnapshot {
  contactId: string;
  initialContactId?: string;
  channelType?: string;
  queue?: string;
  initialCustomerPhoneNumber?: string;
  state?: string;
  participants?: unknown[];
  lastAttributes?: Record<string, unknown>;
  rawContact?: Record<string, unknown>;
  lastUpdatedAt?: string;
}

export interface AwsConnectBridgeState {
  mode: AwsConnectBridgeMode;
  connectionStatus: ConnectionStatus;
  appInstanceId?: string;
  currentContactId?: string;
  agentIdentity?: AwsConnectAgentIdentity;
  currentContact?: AwsConnectContactSnapshot;
}

export interface AwsConnectIncomingSearchContext {
  contact: AwsConnectContactSnapshot;
  agent?: AwsConnectAgentIdentity;
}

export interface AwsConnectWorkspaceBridgeOptions {
  enabled: boolean;
  onStatusChange?: (state: AwsConnectBridgeState) => void;
  onError?: (error: InspectorError) => void;
  onEvent?: (
    event: Omit<
      InspectorEvent,
      'id' | 'sequence' | 'timestamp' | 'timestampMs'
    >,
  ) => void;
  onAgentIdentity?: (agent: AwsConnectAgentIdentity) => void;
  onContactUpdate?: (contact: AwsConnectContactSnapshot) => void;
  onIncomingSearch?: (context: AwsConnectIncomingSearchContext) => void;
  autoFetchAttributes?: boolean;
  autoFetchPhoneNumber?: boolean;
}

export interface AwsConnectBridgeEnvironment {
  insideIframe: boolean;
  awsConnectEnabled: boolean;
}

export interface AwsConnectWorkspaceBridge {
  start: () => Promise<void>;
  stop: () => void;
  reset: () => void;
  getState: () => AwsConnectBridgeState;
  refreshCurrentContact: () => Promise<AwsConnectContactSnapshot | undefined>;
  refreshAgentIdentity: () => Promise<AwsConnectAgentIdentity | undefined>;
}

interface BridgeRuntime {
  cleanup: () => void;
  getContactClient: () => Record<string, unknown> | undefined;
  getAgentClient: () => Record<string, unknown> | undefined;
  getVoiceClient: () => Record<string, unknown> | undefined;
}

const SEARCH_TRIGGER_EVENTS = new Set([
  'onCreate',
  'onIncoming',
  'onConnecting',
  'onPending',
]);

function nowIso(): string {
  return new Date().toISOString();
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : undefined;
}

function getString(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

function getQueueLabel(value: unknown): string | undefined {
  if (typeof value === 'string' && value) {
    return value;
  }

  const record = asRecord(value);
  return getString(record?.name) ?? getString(record?.queueName);
}

function getChannelLabel(value: unknown): string | undefined {
  if (typeof value === 'string' && value) {
    return value;
  }

  return getString(asRecord(value)?.type);
}

function isVoiceChannelType(value: unknown): boolean {
  return getChannelLabel(value)?.toLowerCase() === 'voice';
}

function extractRoutingProfileName(value: unknown): string | undefined {
  const record = asRecord(value);
  return (
    getString(record?.name) ?? getString(asRecord(record?.routingProfile)?.name)
  );
}

function extractRoutingProfileArn(value: unknown): string | undefined {
  const record = asRecord(value);
  return (
    getString(record?.routingProfileARN) ??
    getString(asRecord(record?.routingProfile)?.routingProfileARN)
  );
}

export function createAwsConnectWorkspaceBridge(
  options: AwsConnectWorkspaceBridgeOptions,
): AwsConnectWorkspaceBridge {
  const state: AwsConnectBridgeState = {
    mode: options.enabled ? 'workspace' : 'disabled',
    connectionStatus: options.enabled ? 'STARTING' : 'DESTROYED',
  };

  let runtime: BridgeRuntime | undefined;
  let active = false;
  let started = false;
  let startPromise: Promise<void> | undefined;
  const searchTriggeredContactIds = new Set<string>();

  function emitState(): void {
    options.onStatusChange?.({ ...state });
  }

  function setConnectionStatus(connectionStatus: ConnectionStatus): void {
    state.connectionStatus = connectionStatus;
    emitState();
  }

  function assignContact(
    patch: AwsConnectContactSnapshot,
  ): AwsConnectContactSnapshot {
    const nextContact: AwsConnectContactSnapshot = {
      ...(state.currentContact ?? { contactId: patch.contactId }),
      ...patch,
      contactId: patch.contactId,
      lastUpdatedAt: nowIso(),
    };

    state.currentContactId = nextContact.contactId;
    state.currentContact = nextContact;
    options.onContactUpdate?.(nextContact);
    emitState();
    return nextContact;
  }

  function maybeTriggerIncomingSearch(
    contact: AwsConnectContactSnapshot,
  ): void {
    if (!options.onIncomingSearch) {
      return;
    }

    if (
      !contact.contactId ||
      searchTriggeredContactIds.has(contact.contactId)
    ) {
      return;
    }

    searchTriggeredContactIds.add(contact.contactId);
    options.onIncomingSearch({
      contact,
      agent: state.agentIdentity,
    });
  }

  async function invokeClientMethod<T = unknown>(
    clientFactory: () => Record<string, unknown> | undefined,
    methodName: string,
    args: unknown[],
  ): Promise<T | undefined> {
    const client = clientFactory();
    if (!client) {
      return undefined;
    }

    const method = client[methodName];
    if (typeof method !== 'function') {
      return undefined;
    }

    return (await (
      method as (...innerArgs: unknown[]) => Promise<unknown>
    ).apply(client, args)) as T;
  }

  async function refreshAgentIdentity(): Promise<void> {
    if (!runtime) {
      return;
    }

    const [name, arn, routingProfile, extension, availabilityState] =
      await Promise.all([
        invokeClientMethod<string>(runtime.getAgentClient, 'getName', []),
        invokeClientMethod<string>(runtime.getAgentClient, 'getARN', []),
        invokeClientMethod<Record<string, unknown>>(
          runtime.getAgentClient,
          'getRoutingProfile',
          [],
        ),
        invokeClientMethod<string | undefined>(
          runtime.getAgentClient,
          'getExtension',
          [],
        ),
        invokeClientMethod<Record<string, unknown>>(
          runtime.getAgentClient,
          'getAvailabilityState',
          [],
        ),
      ]);

    const nextIdentity: AwsConnectAgentIdentity = {
      name: name ?? state.agentIdentity?.name,
      arn: arn ?? state.agentIdentity?.arn,
      routingProfileName:
        extractRoutingProfileName(routingProfile) ??
        state.agentIdentity?.routingProfileName,
      routingProfileArn:
        extractRoutingProfileArn(routingProfile) ??
        state.agentIdentity?.routingProfileArn,
      extension: extension ?? state.agentIdentity?.extension,
      availabilityState:
        getString(asRecord(availabilityState)?.name) ??
        getString(asRecord(availabilityState)?.state) ??
        state.agentIdentity?.availabilityState,
      lastUpdatedAt: nowIso(),
    };

    state.agentIdentity = nextIdentity;
    options.onAgentIdentity?.(nextIdentity);
    emitState();
  }

  async function fetchInitialCustomerPhoneNumber(
    contactId: string,
  ): Promise<string | undefined> {
    if (!runtime || options.autoFetchPhoneNumber === false) {
      return undefined;
    }

    const phoneNumber = await invokeClientMethod<string>(
      runtime.getVoiceClient,
      'getInitialCustomerPhoneNumber',
      [contactId],
    );
    if (phoneNumber) {
      assignContact({
        contactId,
        initialCustomerPhoneNumber: phoneNumber,
      });
    }

    return phoneNumber;
  }

  async function fetchAttributes(
    contactId: string,
  ): Promise<Record<string, unknown> | undefined> {
    if (!runtime || options.autoFetchAttributes === false) {
      return undefined;
    }

    const attributes = await invokeClientMethod<Record<string, unknown>>(
      runtime.getContactClient,
      'getAttributes',
      [contactId, '*'],
    );
    if (attributes && typeof attributes === 'object') {
      assignContact({
        contactId,
        lastAttributes: attributes,
      });
      return attributes;
    }

    return undefined;
  }

  async function refreshContact(
    contactId: string,
  ): Promise<AwsConnectContactSnapshot> {
    if (!runtime) {
      return assignContact({ contactId });
    }

    const [
      contactResult,
      participantsResult,
      queueResult,
      channelResult,
      initialContactIdResult,
    ] = await Promise.all([
      invokeClientMethod<Record<string, unknown>>(
        runtime.getContactClient,
        'getContact',
        [contactId],
      ),
      invokeClientMethod<unknown[]>(
        runtime.getContactClient,
        'listParticipants',
        [contactId],
      ),
      invokeClientMethod<unknown>(runtime.getContactClient, 'getQueue', [
        contactId,
      ]),
      invokeClientMethod<unknown>(runtime.getContactClient, 'getChannelType', [
        contactId,
      ]),
      invokeClientMethod<string | undefined>(
        runtime.getContactClient,
        'getInitialContactId',
        [contactId],
      ),
    ]);

    const contactRecord = asRecord(contactResult);
    const nextContact = assignContact({
      contactId,
      initialContactId:
        initialContactIdResult ?? getString(contactRecord?.initialContactId),
      channelType:
        getChannelLabel(channelResult) ??
        getChannelLabel(contactRecord?.channelType) ??
        getChannelLabel(contactRecord?.type),
      queue: getQueueLabel(queueResult) ?? getQueueLabel(contactRecord?.queue),
      state: getString(contactRecord?.state),
      participants: Array.isArray(participantsResult)
        ? participantsResult
        : undefined,
      rawContact: contactRecord,
    });

    if (
      isVoiceChannelType(channelResult) ||
      isVoiceChannelType(contactRecord?.channelType) ||
      isVoiceChannelType(contactRecord?.type)
    ) {
      await fetchInitialCustomerPhoneNumber(contactId);
    }

    await fetchAttributes(contactId);
    return state.currentContact ?? nextContact;
  }

  async function bootstrapContact(
    contactId: string,
    eventName: string,
  ): Promise<void> {
    const contact = await refreshContact(contactId);
    if (SEARCH_TRIGGER_EVENTS.has(eventName)) {
      maybeTriggerIncomingSearch(contact);
    }
  }

  async function start(): Promise<void> {
    if (started && startPromise) {
      await startPromise;
      return;
    }

    started = true;
    active = true;

    if (!options.enabled) {
      state.mode = 'disabled';
      setConnectionStatus('DESTROYED');
      return;
    }

    startPromise = initializeConnect({
      onStatusChange: (connectionStatus) => {
        if (!active) {
          return;
        }

        setConnectionStatus(connectionStatus);
      },
      onEvent: (event) => {
        if (!active) {
          return;
        }

        options.onEvent?.(event);
        if (event.appInstanceId) {
          state.appInstanceId = event.appInstanceId;
          emitState();
        }

        if (
          event.contactId &&
          (event.source === 'lifecycle' || event.source === 'contact')
        ) {
          void bootstrapContact(event.contactId, event.eventName);
        }

        if (
          event.source === 'agent' &&
          (event.eventName === 'onRoutingProfileChanged' ||
            event.eventName === 'onAvailabilityStateChanged' ||
            event.eventName === 'onStateChanged')
        ) {
          void refreshAgentIdentity();
        }
      },
      onError: (error) => {
        if (!active) {
          return;
        }

        options.onError?.({
          id: crypto.randomUUID(),
          timestamp: nowIso(),
          ...error,
        });
      },
      onCapability: () => undefined,
    }).then(async (connectRuntime) => {
      if (!active) {
        connectRuntime.cleanup();
        return;
      }

      runtime = connectRuntime as BridgeRuntime;
      await refreshAgentIdentity();

      if (state.currentContactId) {
        await refreshContact(state.currentContactId);
      }
    });

    await startPromise;
  }

  function stop(): void {
    active = false;
    started = false;
    startPromise = undefined;
    searchTriggeredContactIds.clear();
    runtime?.cleanup();
    runtime = undefined;
    setConnectionStatus('DESTROYED');
  }

  function reset(): void {
    searchTriggeredContactIds.clear();
    state.currentContactId = undefined;
    state.currentContact = undefined;
    state.agentIdentity = undefined;
    state.appInstanceId = undefined;
    emitState();
  }

  return {
    start,
    stop,
    reset,
    getState(): AwsConnectBridgeState {
      return {
        ...state,
        agentIdentity: state.agentIdentity
          ? { ...state.agentIdentity }
          : undefined,
        currentContact: state.currentContact
          ? {
              ...state.currentContact,
              lastAttributes: state.currentContact.lastAttributes
                ? { ...state.currentContact.lastAttributes }
                : undefined,
              participants: state.currentContact.participants
                ? [...state.currentContact.participants]
                : undefined,
              rawContact: state.currentContact.rawContact
                ? { ...state.currentContact.rawContact }
                : undefined,
            }
          : undefined,
      };
    },
    async refreshCurrentContact(): Promise<
      AwsConnectContactSnapshot | undefined
    > {
      if (!state.currentContactId) {
        return undefined;
      }

      return refreshContact(state.currentContactId);
    },
    async refreshAgentIdentity(): Promise<AwsConnectAgentIdentity | undefined> {
      await refreshAgentIdentity();
      return state.agentIdentity;
    },
  };
}

export function shouldEnableAwsConnectBridge(
  environment: AwsConnectBridgeEnvironment,
): boolean {
  return environment.insideIframe && environment.awsConnectEnabled;
}
